/**
 * Auto-accept the Quantcast Choice (Publift Fuse) consent banner for non-GDPR
 * visitors.
 *
 * The CMP is currently configured to show the full IAB-TCF consent wall to ALL
 * regions, including the US — where TCF consent isn't legally required and the
 * wall sits over the signup flow, hurting conversion. For EEA / UK / Switzerland
 * we leave it completely alone (those users must make a real choice). For every
 * other (non-GDPR) region we dismiss it by clicking the CMP's own "Agree" button.
 *
 * Why click "Agree" rather than CSS-hide the banner: hiding the veil with
 * display:none leaves Quantcast in a "consent pending" state that intermittently
 * blocks page interaction (and, with the banner hidden, the user can't accept it
 * to unlock) — that regression is what broke the flow before. Dismissing through
 * the CMP's own button releases that state cleanly. Verified on the live CMP:
 * a scripted Agree-click removes the wall and the page stays fully interactive.
 *
 * Geo source: a FRESH per-pageview lookup (Cloudflare CF-IPCountry via
 * `/dictionaries/countries/current`) — NOT the persisted `currentCountry`, which
 * is rehydrated from localStorage without a refresh for returning visitors and so
 * can be stale (a US→EU traveler would otherwise be wrongly auto-consented). Fail
 * safe: any error, unknown code, or EEA/UK/CH leaves the banner untouched.
 */

import { getCurrentCountry } from 'src/modules/common/api';

// EEA + UK + Switzerland — the regions where the banner stays and the user chooses.
const KEEP = new Set(
  'at be bg hr cy cz dk ee fi fr de gr el hu ie it lv lt lu mt nl pl pt ro sk si es se is li no gb uk ch'.split(' '),
);

const AGREE_SELECTOR = '#qc-cmp2-ui button, .qc-cmp-cleanslate button, #qc-cmp2-container button';

// Poll the CMP for ~90s. It normally renders within a few seconds, but a slow
// network / deferred scripts can delay it — so the window is deliberately wide
// (Finding 2: a 20s cap missed late renders). Capped so it can't poll forever if
// the CMP never appears (consent already stored, ad/CMP blocked, etc.).
const POLL_MS = 500;
const MAX_TRIES = 180;

let started = false; // the fresh-geo lookup is kicked off exactly once
let watching = false; // the dismissal watcher is running / done

function clickAgree() {
  const agree = Array.from(document.querySelectorAll(AGREE_SELECTOR)).find((b) => {
    const t = (b.textContent || '').trim().toLowerCase();
    // The primary "Agree"/"Accept" action — not "More options" / "Manage" / "Reject".
    return /agree|accept/.test(t) && !/more|option|manage|setting|reject|disagree|partner/.test(t);
  });
  if (agree) {
    agree.click();
    return true;
  }
  return false;
}

// Eligibility (non-EU, decided once from the fresh geo) is kept separate from
// dismissal: once eligible, keep trying until the CMP button is actually clicked.
function watchAndDismiss() {
  if (watching) return;
  watching = true;
  if (clickAgree()) return;
  let tries = 0;
  const id = window.setInterval(() => {
    tries += 1;
    if (clickAgree() || tries >= MAX_TRIES) window.clearInterval(id);
  }, POLL_MS);
}

export default function autoAcceptCmpForNonEu() {
  if (started || typeof document === 'undefined') return;
  started = true;
  getCurrentCountry()
    .then((res) => res.json())
    .then((country) => {
      const code = String((country && country.code) || '').toLowerCase();
      if (!/^[a-z]{2}$/.test(code) || KEEP.has(code)) return; // EU / unknown → leave banner
      watchAndDismiss(); // non-GDPR → dismiss the wall when the CMP renders
    })
    .catch(() => {}); // network/parse error → leave banner (safe default)
}
